/**
 * Cache Factory
 *
 * Automatically selects the best available storage mechanism for the current platform:
 *
 * 1. **Capacitor native (Android/iOS) → CapacitorFilesystemCache** (Directory.Data,
 *    OS-level files, persistent across reboot/kill/storage pressure)
 * 2. Tizen TV → TizenMediaCache (Filesystem API with file:// URLs)
 * 3. Browser/PWA → MediaCache (Cache API with blob:// URLs)
 * 4. Android/HTTP → IndexedDBCache (IndexedDB with blob:// URLs)
 * 5. Fallback → NoopCache (streaming only)
 *
 * Selection priority considers:
 * - Capacitor native detection (highest priority on mobile — solves browser
 *   storage eviction problem observed in prod Tab A7: blobs were lost after
 *   app kill because WebView denied persistent storage)
 * - Platform detection (Tizen, browser)
 * - Secure context requirement (Cache API needs HTTPS)
 * - API availability checks
 */

import {
  hasNativeMediaBridge,
  isEmbeddedPlatform,
  waitForNativeMediaBridge,
} from './embeddedHostBridge';
import { IMediaCache } from './types';

const LOG_PREFIX = '[CacheFactory]';

// Lazy imports to avoid loading unnecessary modules
let _capacitorFsCache: IMediaCache | null = null;
let _browserCache: IMediaCache | null = null;
let _tizenCache: IMediaCache | null = null;
let _embeddedCache: IMediaCache | null = null;
let _indexedDBCache: IMediaCache | null = null;
let _noopCache: IMediaCache | null = null;

/**
 * Checks if we're running inside a Capacitor native shell (Android or iOS).
 *
 * On native, `window.Capacitor.isNativePlatform()` returns true and the
 * Filesystem plugin is bridged to the OS. On pure browser (no Capacitor)
 * the plugin isn't available so we fall back to BrowserCache/IndexedDB.
 *
 * Note: Tizen TV is a different shell — it has its own `window.tizen` and
 * is handled by a separate branch in `detectStorageCapabilities()`.
 */
function isCapacitorNative(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (
    window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
  ).Capacitor;
  return typeof cap?.isNativePlatform === 'function' && cap.isNativePlatform();
}

/**
 * Checks if we're running on Samsung Tizen TV.
 */
function isTizen(): boolean {
  return typeof window !== 'undefined' && typeof window.tizen !== 'undefined';
}

/**
 * Checks if we're running EMBEDDED (WebView-in-host / AAR) AND the host ALREADY
 * exposes the NATIVE media cache bridge (`EffluxHost.cacheMedia`/`isMediaCached`).
 *
 * Só roteia pro EmbeddedMediaCache quando o host sabe baixar/servir mídia nativa
 * (cura do leak de VA — bytes fora do renderer). Host embedded ANTIGO (só
 * `__effluxEmbedded`, sem a bridge de mídia) → false → cai no MediaCache do
 * browser (back-compat: SW/Cache-API/blob, como antes deste adapter). O gate por
 * versão evita quebrar hosts que ainda não trazem o `EffluxMediaStore`.
 *
 * ⚠️ Síncrono — responde "ainda não" enquanto o `addJavascriptInterface` do AAR
 * não aterrissou. Só `detectStorageCapabilities` (diagnóstico) usa este snapshot;
 * a DECISÃO memoizada é do `createMediaCache`, que ESPERA a bridge.
 */
function isEmbeddedWithNativeMedia(): boolean {
  return isEmbeddedPlatform() && hasNativeMediaBridge();
}

/**
 * Checks if Tizen Filesystem API is available.
 */
function tizenFilesystemAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.tizen !== 'undefined' &&
    typeof window.tizen.filesystem !== 'undefined'
  );
}

/**
 * Checks if Cache API is available and usable.
 * Cache API requires HTTPS or localhost (secure context).
 */
function cacheAPIAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof caches !== 'undefined' &&
    'open' in caches &&
    window.isSecureContext === true
  );
}

/**
 * Checks if IndexedDB is available.
 */
function indexedDBAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

/**
 * Detects the current platform and available storage options.
 */
export function detectStorageCapabilities(): {
  platform: 'capacitor-native' | 'tizen' | 'embedded' | 'browser' | 'unknown';
  isSecureContext: boolean;
  capacitorNative: boolean;
  embedded: boolean;
  tizenFilesystem: boolean;
  cacheAPI: boolean;
  indexedDB: boolean;
  recommendation:
    | 'capacitor-filesystem'
    | 'tizen'
    | 'embedded'
    | 'browser'
    | 'indexeddb'
    | 'noop';
} {
  // `platform` vem do marcador `__effluxEmbedded` (injetado ANTES do bundle →
  // nunca corre); `embedded` (que dirige a recommendation) exige a bridge de
  // mídia presente AGORA. Separar os dois é o que impede o host atrasado de se
  // disfarçar de 'browser'.
  const embedded = isEmbeddedWithNativeMedia();
  const capacitorNative = isCapacitorNative();
  const platform = isEmbeddedPlatform()
    ? 'embedded'
    : capacitorNative
      ? 'capacitor-native'
      : isTizen()
        ? 'tizen'
        : typeof window !== 'undefined'
          ? 'browser'
          : 'unknown';
  const isSecureContext =
    typeof window !== 'undefined' && window.isSecureContext === true;
  const tizenFilesystem = tizenFilesystemAvailable();
  const cacheAPI = cacheAPIAvailable();
  const indexedDB = indexedDBAvailable();

  // Determine recommendation. Order matters — first match wins.
  //
  // 1. Capacitor native (Android/iOS): persistent storage immune to WebView
  //    eviction. **THIS is what fixes the "app kill = cache vaporizes" bug**
  //    observed in prod Tab A7. Must come BEFORE 'browser' because Capacitor
  //    WebView also exposes a secure-context Cache API, but blobs there get
  //    evicted under storage pressure.
  // 2. Tizen TV: roteado pra TizenMediaCache (Filesystem API com chunked write).
  //    Histórico:
  //    - v1.0.10 (2026-05-24): TizenMediaCache crashava WGT em UN50U8600F
  //      durante write de blobs grandes — sync writeBlob de blob inteiro
  //      travava o WebView, watchdog matava o app.
  //    - v1.0.13: rollback pra NoopCache até reescrita.
  //    - v1.0.22 (2026-05-26 manhã): roteamos pra IndexedDBCache — funcionou
  //      mas quota do WebView Tizen é só ~80 MB, insuficiente pra playlist
  //      real (vídeo 4K sozinho > 40 MB).
  //    - v1.0.23 (hoje): TizenMediaCache reescrito com chunked write
  //      (256 KB/chunk + yield ao event loop a cada 16 chunks). Filesystem
  //      API tem quota ~500 MB+ E retorna file:// URLs que contornam o HW
  //      overlay no snapshot ao vivo do Tizen Consumer 9.0.
  //
  //    Fallback safety: se Tizen Filesystem API não estiver disponível
  //    (browsers de dev, simulator), cai pra IndexedDB ou NoopCache.
  // 3. Browser secure context: Cache API
  // 4. Browser without secure context: IndexedDB fallback
  // 5. Nothing: NoopCache (streaming only)
  let recommendation:
    | 'capacitor-filesystem'
    | 'tizen'
    | 'embedded'
    | 'browser'
    | 'indexeddb'
    | 'noop' = 'noop';

  if (embedded) {
    // Embedded (AAR) com bridge nativa de mídia → EmbeddedMediaCache. DEVE vir
    // ANTES do 'browser'/cacheAPI: o WebView embedded também expõe Cache API
    // (secure context), mas servir mídia por lá (SW/blob) vaza VA no renderer
    // 32-bit → renderer-kill. O cache nativo serve do disco (bytes fora da rede
    // do renderer), igual à frota Capacitor.
    recommendation = 'embedded';
  } else if (capacitorNative) {
    recommendation = 'capacitor-filesystem';
  } else if (platform === 'tizen') {
    // 2026-05-28 (v1.0.60) — REATIVADO TizenMediaCache.
    //
    // Histórico:
    // - v1.0.22: roteamos pra IndexedDB porque TizenMediaCache crashava.
    //   Funcionou estável, MAS WebView Tizen 9.0 capa quota IDB em 80 MB.
    // - v1.0.24: tentamos TizenMediaCache de novo, crashou no boot.
    //   Suspeita era checkAvailability() síncrono no construtor.
    // - v1.0.59 (telemetria): Renato confirmou empiricamente o problema
    //   da quota IDB de 80 MB. Storage atingiu 188% (150.5MB / 80MB) e
    //   travou completamente. `abort()` de transactions IDB no Tizen
    //   NÃO libera bytes — lixo acumula até o disco "encher" virtualmente.
    // - v1.0.60: TizenMediaCache foi blindado contra o crash de boot
    //   (constructor agora minimal, defer chamadas síncronas a APIs
    //   nativas). Filesystem API tem ~500 MB de quota, abort/delete
    //   liberam espaço corretamente, file:// URLs contornam HW overlay.
    //
    // Fallback: se Filesystem API indisponível (simulator, dev), cai pra
    // IndexedDB pra app não quebrar.
    if (tizenFilesystem) {
      recommendation = 'tizen';
    } else if (indexedDB) {
      recommendation = 'indexeddb';
    }
  } else if (cacheAPI) {
    recommendation = 'browser';
  } else if (indexedDB) {
    recommendation = 'indexeddb';
  }

  return {
    platform,
    isSecureContext,
    capacitorNative,
    embedded,
    tizenFilesystem,
    cacheAPI,
    indexedDB,
    recommendation,
  };
}

/**
 * Resolves which cache to build, awaiting the embedded host bridge when needed.
 *
 * O `EffluxHost` do AAR (`addJavascriptInterface`) pode não estar visível pros
 * primeiros scripts do documento. Como esta decisão é MEMOIZADA pra sessão
 * inteira (aqui e no `media.store`), decidir pelo snapshot síncrono perdia a
 * corrida e prendia o embedded no MediaCache do browser PARA SEMPRE — blob +
 * Service Worker + fetch→Blob DENTRO do renderer, exatamente o que estoura o
 * espaço de endereçamento do renderer 32-bit (renderer-kill).
 *
 * Por isso o embedded ESPERA a bridge (mesma regra do
 * `EmbeddedMediaCache.waitForHost`). A espera é curta e conclusiva:
 * - bridge presente → 'embedded';
 * - `EffluxHost` presente SEM os métodos de mídia → AAR antigo, decisão
 *   imediata (zero espera) → segue a cadeia normal → browser cache;
 * - `EffluxHost` que nunca aparece → paga o timeout 1× e cai no browser cache.
 *
 * Demais plataformas (android/tizen/browser) não passam por aqui.
 */
async function resolveRecommendation(): Promise<
  ReturnType<typeof detectStorageCapabilities>['recommendation']
> {
  if (isEmbeddedPlatform() && !hasNativeMediaBridge()) {
    console.log(
      `${LOG_PREFIX} ⏳ Embedded: aguardando a bridge nativa de mídia (EffluxHost)...`,
    );
    const bridge = await waitForNativeMediaBridge();
    if (!bridge) {
      console.warn(
        `${LOG_PREFIX} ⚠️ Embedded SEM bridge nativa de mídia (AAR antigo) — fallback pro browser cache`,
      );
    }
  }
  return detectStorageCapabilities().recommendation;
}

/**
 * Creates and returns the appropriate media cache for the current platform.
 *
 * Selection order:
 * 1. Embedded (AAR) with the native media bridge → EmbeddedMediaCache
 * 2. Capacitor native → CapacitorFilesystemCache
 * 3. Tizen with Filesystem API → TizenMediaCache
 * 4. Secure context with Cache API → MediaCache (Browser)
 * 5. IndexedDB available → IndexedDBCache
 * 6. Nothing available → NoopCache (streaming only)
 */
export async function createMediaCache(): Promise<IMediaCache> {
  console.log(`${LOG_PREFIX} 🔍 Detecting storage capabilities...`);

  const recommendation = await resolveRecommendation();
  const capabilities = { ...detectStorageCapabilities(), recommendation };

  console.log(`${LOG_PREFIX}    └─ Platform: ${capabilities.platform}`);
  console.log(
    `${LOG_PREFIX}    └─ Secure context: ${capabilities.isSecureContext}`,
  );
  console.log(
    `${LOG_PREFIX}    └─ Capacitor native: ${capabilities.capacitorNative}`,
  );
  console.log(`${LOG_PREFIX}    └─ Embedded (AAR): ${capabilities.embedded}`);
  console.log(
    `${LOG_PREFIX}    └─ Tizen Filesystem: ${capabilities.tizenFilesystem}`,
  );
  console.log(`${LOG_PREFIX}    └─ Cache API: ${capabilities.cacheAPI}`);
  console.log(`${LOG_PREFIX}    └─ IndexedDB: ${capabilities.indexedDB}`);
  console.log(
    `${LOG_PREFIX}    └─ Recommendation: ${capabilities.recommendation}`,
  );

  switch (capabilities.recommendation) {
    case 'embedded':
      console.log(
        `${LOG_PREFIX} ✅ Using EmbeddedMediaCache (host nativo — AAR)`,
      );
      console.log(
        `${LOG_PREFIX}    └─ Download + serve NATIVOS (bytes fora da rede do renderer → sem leak de VA)`,
      );
      console.log(
        `${LOG_PREFIX}    └─ Playback: https://player.efflux.media/__efflux_media__/<id>`,
      );
      if (!_embeddedCache) {
        const { default: embeddedMediaCache } =
          await import('./EmbeddedMediaCache');
        _embeddedCache = embeddedMediaCache;
      }
      return _embeddedCache;

    case 'capacitor-filesystem':
      console.log(
        `${LOG_PREFIX} ✅ Using CapacitorFilesystemCache (OS-level files)`,
      );
      console.log(
        `${LOG_PREFIX}    └─ Persistent across reboot/kill/storage pressure`,
      );
      console.log(
        `${LOG_PREFIX}    └─ Returns http://localhost/_capacitor_file_/... URLs`,
      );
      if (!_capacitorFsCache) {
        const { default: capacitorFsCache } =
          await import('./CapacitorFilesystemCache');
        _capacitorFsCache = capacitorFsCache;
      }
      return _capacitorFsCache;

    case 'tizen':
      console.log(`${LOG_PREFIX} ✅ Using TizenMediaCache (Filesystem API)`);
      console.log(
        `${LOG_PREFIX}    └─ Returns file:// URLs for video playback`,
      );
      if (!_tizenCache) {
        const { default: tizenMediaCache } = await import('./TizenMediaCache');
        _tizenCache = tizenMediaCache;
      }
      return _tizenCache;

    case 'browser':
      console.log(`${LOG_PREFIX} ✅ Using MediaCache (Cache API)`);
      console.log(
        `${LOG_PREFIX}    └─ Returns blob:// URLs for video playback`,
      );
      if (!_browserCache) {
        const { default: browserMediaCache } = await import('./MediaCache');
        _browserCache = browserMediaCache;
      }
      return _browserCache;

    case 'indexeddb':
      console.log(`${LOG_PREFIX} ✅ Using IndexedDBCache (fallback)`);
      console.log(
        `${LOG_PREFIX}    └─ Returns blob:// URLs for video playback`,
      );
      console.log(
        `${LOG_PREFIX}    └─ Note: Cache API unavailable (not secure context)`,
      );
      if (!_indexedDBCache) {
        const { default: indexedDBCache } = await import('./IndexedDBCache');
        _indexedDBCache = indexedDBCache;
      }
      return _indexedDBCache;

    default:
      console.warn(`${LOG_PREFIX} ⚠️ Using NoopCache (streaming only)`);
      console.warn(`${LOG_PREFIX}    └─ No storage mechanism available`);
      console.warn(
        `${LOG_PREFIX}    └─ Media will be streamed directly from remote URLs`,
      );
      if (!_noopCache) {
        const { default: noopCache } = await import('./NoopCache');
        _noopCache = noopCache;
      }
      return _noopCache;
  }
}

/**
 * Gets a cached instance without creating a new one.
 * Returns null if no cache has been created yet.
 */
export function getExistingCache(): IMediaCache | null {
  return (
    _capacitorFsCache ||
    _tizenCache ||
    _embeddedCache ||
    _browserCache ||
    _indexedDBCache ||
    _noopCache
  );
}

/**
 * Resets all cached instances.
 * Useful for testing or when you need to re-detect storage capabilities.
 */
export function resetCacheFactory(): void {
  console.log(`${LOG_PREFIX} 🔄 Resetting cache factory`);
  _capacitorFsCache = null;
  _browserCache = null;
  _tizenCache = null;
  _embeddedCache = null;
  _indexedDBCache = null;
  _noopCache = null;
}

/**
 * Returns the type of cache currently in use.
 */
export function getCurrentCacheType():
  | 'capacitor-filesystem'
  | 'tizen'
  | 'embedded'
  | 'browser'
  | 'indexeddb'
  | 'noop'
  | 'none' {
  if (_capacitorFsCache) return 'capacitor-filesystem';
  if (_tizenCache) return 'tizen';
  if (_embeddedCache) return 'embedded';
  if (_browserCache) return 'browser';
  if (_indexedDBCache) return 'indexeddb';
  if (_noopCache) return 'noop';
  return 'none';
}
